import { useSessionStorageState } from 'ahooks'
import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import axios from '../utils/axios'
import { HOST_API_KEY } from '../config-global'
import { useSettingsContext } from '../components/settings'
import SimpleLoading from '../components/simple-loading/SimpleLoading'
import axiosEventz from 'src/utils/axiosEventz'

const WhiteLabelContext = createContext({})

export function WhiteLabelProvider({ children }) {
  const { onUpdatePresetsColor, setThemeMode } = useSettingsContext()

  const storageConfig = { defaultValue: {} }

  const [whitelabel, setWhitelabel] = useSessionStorageState('whitelabel', storageConfig)
  const [eventzWhitelabel, setEventzWhitelabel] = useSessionStorageState(
    'eventzWhitelabel',
    storageConfig
  )

  const [isLoadingEventz, setIsLoadingEventz] = useState(false)

  useEffect(() => {
    async function fetchWhitelabel() {
      try {
        const { data } = await axios.get(`${HOST_API_KEY}/api/config/tenant`)
        setWhitelabel(data)
      } catch (err) {
        console.error('Failed to fetch whitelabel configuration:', err)
      }
    }

    if (!whitelabel?.id) {
      fetchWhitelabel()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchEventzWhitelabel = useCallback(async () => {
    try {
      setIsLoadingEventz(true)
      const { data } = await axiosEventz.get('/api/tenant')
      setEventzWhitelabel(data)
    } catch (err) {
      console.error('Failed to fetch whitelabel:', err)
    } finally {
      setIsLoadingEventz(false)
    }
  }, [setEventzWhitelabel])

  useEffect(() => {
    const tenantId = localStorage.getItem('tenantId')
    if (!eventzWhitelabel?.id && tenantId) {
      fetchEventzWhitelabel()
    }
  }, [fetchEventzWhitelabel, eventzWhitelabel])

  useEffect(() => {
    if (whitelabel?.id) {
      if (whitelabel.theme?.palette?.primary) {
        onUpdatePresetsColor(whitelabel.theme.palette.primary)
      }
      if (whitelabel.theme?.mode) {
        setThemeMode(whitelabel.theme.mode)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whitelabel])

  if (!whitelabel?.id) {
    return <SimpleLoading />
  }

  return (
    <WhiteLabelContext.Provider
      value={{
        whitelabel,
        eventzWhitelabel,
        isLoadingEventz,
        fetchEventzWhitelabel,
      }}
    >
      {children}
    </WhiteLabelContext.Provider>
  )
}

WhiteLabelProvider.propTypes = {
  children: PropTypes.node,
}

export function useWhiteLabel() {
  const context = useContext(WhiteLabelContext)
  if (context === null) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider')
  }
  return context
}
